import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import TopicPickerMulti from '../../components/shared/TopicPickerMulti';
import { 
  ChevronLeft, Plus, Edit, Trash2, Save, CheckCircle, 
  Clock, Image, FileText, Loader2, X, Upload, Link2,
  Search, Copy, AlertTriangle, RefreshCw, Info, Calendar, ChevronsUpDown, Check,
  Flag, ChevronDown, ChevronRight, User, XCircle, FolderOpen, BookOpen, ClipboardPen, Eye,
  Maximize2, Minimize2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import ImagePickerModal from '../../components/common/ImagePickerModal';
import PictureManagerUploadDialog from '../../components/common/PictureManagerUploadDialog';
import ResearchPanel from '../../components/common/ResearchPanel';
import ReviewChip from '../../components/admin/ReviewChip';
import ReviewBadge from '../../components/admin/ReviewBadge';
import QuestionViewModal from '../../components/admin/QuestionViewModal';
import useReviewConfig from '../../hooks/useReviewConfig';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Report type labels
const REPORT_TYPE_LABELS = {
  incorrect_answer: 'Incorrect Answer',
  incorrect_question: 'Incorrect Question',
  outdated_information: 'Outdated Information',
  duplicate_question: 'Duplicate Question',
  other: 'Other'
};

const WrittenPaperDetailPage = () => {
  const navigate = useNavigate();
  const { paperId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // Delete of a written question is guarded server-side by
  // `get_current_full_admin` (routes/written.py:1299). Sub-admins would
  // just get a 403 toast, so hide the entry point entirely.
  const isFullAdmin = user?.role === 'admin';
  // Iter 113: Needs Review workflow
  const { enabled: reviewEnabled, categories: reviewCategories, getCategory: getReviewCategory } = useReviewConfig();
  const [reviewFilter, setReviewFilter] = useState('any'); // 'any' | 'true'
  const [reviewCategoryFilter, setReviewCategoryFilter] = useState('all');
  
  const [paper, setPaper] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flaggedQuestionIds, setFlaggedQuestionIds] = useState([]);
  
  // Question modal state
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [answerExpanded, setAnswerExpanded] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_images: [],
    answer_text: '',
    answer_images: [],
    topic_id: '',
    topic_ids: [],
    marks: '',
    is_answered: false,
    video_url: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Report-related states
  const [questionReports, setQuestionReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [resolvingReport, setResolvingReport] = useState(null);
  const [showReportsSection, setShowReportsSection] = useState(false);
  const [expandedReportIds, setExpandedReportIds] = useState(new Set());
  
  // Duplicate detection state
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [similarMeta, setSimilarMeta] = useState({ threshold_used: null, candidates_total: 0, skipped_no_embedding: 0 });
  const [showDuplicatesPanel, setShowDuplicatesPanel] = useState(false);
  // Which duplicate cards are expanded to show the full question text +
  // answer. Reset whenever the duplicate panel closes so the next check
  // starts from a compact list. Keyed by question id.
  const [expandedDupIds, setExpandedDupIds] = useState(new Set());
  const [showResearchSheet, setShowResearchSheet] = useState(false);
  
  // Link question modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedSimilarQuestion, setSelectedSimilarQuestion] = useState(null);
  const [answerAction, setAnswerAction] = useState('keep');
  const [linkingQuestion, setLinkingQuestion] = useState(false);
  
  // Image upload states
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerType, setImagePickerType] = useState('question'); // 'question' or 'answer'
  const [pmUploadOpen, setPmUploadOpen] = useState(false);
  const [pmUploadTarget, setPmUploadTarget] = useState('answer'); // 'question' | 'answer'
  
  // Topic combobox state
  const [topicComboOpen, setTopicComboOpen] = useState(false);
  const [topicSearchQuery, setTopicSearchQuery] = useState('');
  
  // Question filter state (for clickable info tags)
  const [activeFilter, setActiveFilter] = useState(null); // null | 'answered' | 'unanswered' | 'flagged' | 'no_topic'

  useEffect(() => {
    fetchPaperData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperId]);

  // Honor ?openQuestion={id} deep-links (from WrittenSearchModal).
  // Wait for questions to be loaded, then auto-open the edit dialog and
  // scroll the matching card into view. Strip the param after open so a
  // refresh doesn't keep re-opening the dialog.
  useEffect(() => {
    const openId = searchParams.get('openQuestion');
    if (!openId || questions.length === 0) return;
    const match = questions.find(q => q.id === openId);
    if (match) {
      openEditQuestionModal(match);
      // Scroll the card into view after the modal mounts.
      setTimeout(() => {
        const el = document.querySelector(`[data-question-id="${openId}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    } else {
      toast.error(`Question ${openId} not found in this paper`);
    }
    // Remove the param so reopening manually works as expected.
    searchParams.delete('openQuestion');
    setSearchParams(searchParams, { replace: true });
  }, [questions, searchParams]);

  const fetchPaperData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/written/papers/${paperId}`);
      setPaper(response.data.paper);
      setQuestions(response.data.questions || []);
      setTopics(response.data.topics || []);
      setFlaggedQuestionIds(response.data.flagged_question_ids || []);
    } catch (error) {
      console.error('Failed to fetch paper:', error);
      toast.error('Failed to load paper');
      navigate('/admin/written');
    } finally {
      setLoading(false);
    }
  };

  const openAddQuestionModal = () => {
    setEditingQuestion(null);
    setQuestionForm({
      question_text: '',
      question_images: [],
      answer_text: '',
      answer_images: [],
      topic_id: '',
      topic_ids: [],
      marks: '',
      is_answered: false,
      video_url: ''
    });
    setTopicSearchQuery('');
    setSimilarQuestions([]);
    setShowDuplicatesPanel(false);
    setShowQuestionModal(true);
  };

  const openEditQuestionModal = async (question) => {
    setEditingQuestion(question);
    setAnswerExpanded(false);
    // Reconcile legacy topic_id + new topic_ids → single source of truth
    const incomingTopicIds = question.topic_ids?.length > 0
      ? question.topic_ids
      : (question.topic_id ? [question.topic_id] : []);
    setQuestionForm({
      question_text: question.question_text || '',
      question_images: question.question_images || [],
      answer_text: question.answer_text || '',
      answer_images: question.answer_images || [],
      topic_id: incomingTopicIds[0] || '',
      topic_ids: incomingTopicIds,
      marks: question.marks?.toString() || '',
      is_answered: question.is_answered || false,
      video_url: question.video_url || ''
    });
    setTopicSearchQuery('');
    setSimilarQuestions([]);
    setShowDuplicatesPanel(false);
    setShowQuestionModal(true);
    
    // Fetch reports for this question
    setLoadingReports(true);
    try {
      const reportsResponse = await axios.get(`${API}/reports/admin/written/question/${question.id}`);
      const reports = reportsResponse.data.reports || [];
      setQuestionReports(reports);
      
      // Determine which reports are active (pending or under_review)
      const activeReportIds = reports
        .filter(r => r.status === 'pending' || r.status === 'under_review')
        .map(r => r.id);
      
      // If there are active reports, expand the section and those reports
      if (activeReportIds.length > 0) {
        setShowReportsSection(true);
        setExpandedReportIds(new Set(activeReportIds));
      } else {
        // No active reports - collapse everything
        setShowReportsSection(false);
        setExpandedReportIds(new Set());
      }
    } catch (error) {
      console.error('Failed to fetch question reports:', error);
      setQuestionReports([]);
      setShowReportsSection(false);
      setExpandedReportIds(new Set());
    } finally {
      setLoadingReports(false);
    }
  };

  // Check for duplicate questions
  const handleCheckDuplicates = async () => {
    if (!questionForm.question_text.trim() || questionForm.question_text.length < 10) {
      toast.error('Please enter at least 10 characters to check for duplicates');
      return;
    }
    
    try {
      setCheckingDuplicates(true);
      const response = await axios.post(`${API}/written/questions/find-similar`, {
        question_text: questionForm.question_text,
        grade: paper.grade,
        function_id: paper.function_id,
        exclude_question_id: editingQuestion?.id || null
      });
      
      const similar = response.data.similar_questions || [];
      setSimilarQuestions(similar);
      setSimilarMeta({
        threshold_used: response.data.threshold_used ?? null,
        candidates_total: response.data.candidates_total ?? 0,
        skipped_no_embedding: response.data.skipped_no_embedding ?? 0,
      });
      setShowDuplicatesPanel(true);
      setExpandedDupIds(new Set()); // fresh check → collapse all cards
      
      if (similar.length === 0) {
        toast.success('No similar questions found - this appears to be unique!');
      } else {
        toast.info(`Found ${similar.length} similar question(s)`);
      }
    } catch (error) {
      console.error('Failed to check duplicates:', error);
      toast.error('Failed to check for duplicates');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Open link question modal
  const openLinkQuestionModal = (similarQ) => {
    setSelectedSimilarQuestion(similarQ);
    setAnswerAction('keep');
    setShowLinkModal(true);
  };

  // Link existing question to paper
  const handleLinkQuestion = async () => {
    if (!selectedSimilarQuestion) return;
    
    try {
      setLinkingQuestion(true);
      await axios.post(
        `${API}/written/papers/${paperId}/questions/${selectedSimilarQuestion.question.id}/link`,
        {
          question_id: selectedSimilarQuestion.question.id,
          answer_action: answerAction,
          new_answer: answerAction !== 'keep' ? questionForm.answer_text : null
        }
      );
      
      toast.success('Question linked successfully!');
      setShowLinkModal(false);
      setShowQuestionModal(false);
      fetchPaperData();
    } catch (error) {
      console.error('Failed to link question:', error);
      toast.error(error.response?.data?.detail || 'Failed to link question');
    } finally {
      setLinkingQuestion(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        ...questionForm,
        marks: questionForm.marks ? parseInt(questionForm.marks) : null,
        topic_ids: questionForm.topic_ids || [],
        topic_id: (questionForm.topic_ids && questionForm.topic_ids[0]) || null,
      };
      
      if (editingQuestion) {
        const response = await axios.put(`${API}/written/questions/${editingQuestion.id}`, payload);
        // Show warning if question is shared
        if (response.data.is_shared) {
          toast.warning(`Question updated! Note: This question appears in ${response.data.appears_in_count} papers.`);
        } else {
          toast.success('Question updated successfully');
        }
      } else {
        await axios.post(`${API}/written/papers/${paperId}/questions`, payload);
        toast.success('Question added successfully');
      }
      
      setShowQuestionModal(false);
      fetchPaperData();
    } catch (error) {
      console.error('Failed to save question:', error);
      toast.error('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!questionForm.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    try {
      setSaving(true);
      const payload = {
        ...questionForm,
        marks: questionForm.marks ? parseInt(questionForm.marks) : null,
        topic_ids: questionForm.topic_ids || [],
        topic_id: (questionForm.topic_ids && questionForm.topic_ids[0]) || null,
      };
      
      await axios.post(`${API}/written/papers/${paperId}/questions`, payload);
      toast.success('Question added! Add another...');
      
      // Reset form for next question
      setQuestionForm({
        question_text: '',
        question_images: [],
        answer_text: '',
        answer_images: [],
        topic_id: questionForm.topic_id, // Keep the topic
        topic_ids: questionForm.topic_ids,
        marks: '',
        is_answered: false,
        video_url: ''
      });
      setSimilarQuestions([]);
      setShowDuplicatesPanel(false);
      
      fetchPaperData();
    } catch (error) {
      console.error('Failed to save question:', error);
      toast.error('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  // Handler for "Update & Resolve Reports" - updates question and resolves all pending reports
  const handleUpdateAndResolve = async () => {
    if (!editingQuestion) return;

    try {
      setSaving(true);
      const payload = {
        ...questionForm,
        marks: questionForm.marks ? parseInt(questionForm.marks) : null,
        topic_ids: questionForm.topic_ids || [],
        topic_id: (questionForm.topic_ids && questionForm.topic_ids[0]) || null,
      };
      
      // First update the question
      await axios.put(`${API}/written/questions/${editingQuestion.id}`, payload);
      
      // Then resolve all pending reports
      const pendingReports = questionReports.filter(r => r.status === 'pending' || r.status === 'under_review');
      if (pendingReports.length > 0) {
        await axios.put(`${API}/reports/admin/written/resolve-all/${editingQuestion.id}?status=resolved`, null, {
          params: { admin_notes: 'Question updated based on report' }
        });
      }
      
      toast.success(`Question updated and ${pendingReports.length} report(s) resolved`);
      setShowQuestionModal(false);
      setQuestionReports([]);
      setShowReportsSection(false);
      setExpandedReportIds(new Set());
      fetchPaperData();
    } catch (error) {
      console.error('Failed to update question and resolve reports:', error);
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSaving(false);
    }
  };
  
  // Handler to resolve a single report
  const handleResolveReport = async (reportId) => {
    setResolvingReport(reportId);
    try {
      await axios.put(`${API}/reports/admin/written/${reportId}`, {
        status: 'resolved',
        admin_notes: 'Resolved by admin'
      });
      toast.success('Report resolved');
      // Refresh reports list
      setQuestionReports(prev => prev.map(r => 
        r.id === reportId ? { ...r, status: 'resolved' } : r
      ));
      fetchPaperData();
    } catch (error) {
      console.error('Failed to resolve report:', error);
      toast.error('Failed to resolve report');
    } finally {
      setResolvingReport(null);
    }
  };
  
  // Handler to dismiss a single report
  const handleDismissReport = async (reportId) => {
    setResolvingReport(reportId);
    try {
      await axios.put(`${API}/reports/admin/written/${reportId}`, {
        status: 'dismissed',
        admin_notes: 'Dismissed by admin'
      });
      toast.success('Report dismissed');
      // Refresh reports list
      setQuestionReports(prev => prev.map(r => 
        r.id === reportId ? { ...r, status: 'dismissed' } : r
      ));
      fetchPaperData();
    } catch (error) {
      console.error('Failed to dismiss report:', error);
      toast.error('Failed to dismiss report');
    } finally {
      setResolvingReport(null);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    // Defense in depth: server blocks sub-admins with 403 via
    // get_current_full_admin; suppress the client-side call too so we
    // never fire a request that we know will fail.
    if (!isFullAdmin) {
      toast.error('Only full admins can delete questions.');
      return;
    }
    // Check if question is shared
    try {
      const response = await axios.get(`${API}/written/questions/${questionId}`);
      const isShared = response.data.is_shared;
      const appearCount = response.data.appears_in_count;
      
      let confirmMsg = 'Are you sure you want to delete this question?';
      if (isShared) {
        confirmMsg = `This question appears in ${appearCount} papers. Do you want to remove it from this paper only? (The question will still exist in other papers)`;
      }
      
      if (!window.confirm(confirmMsg)) return;
      
      await axios.delete(`${API}/written/questions/${questionId}?paper_id=${paperId}`);
      toast.success(isShared ? 'Question removed from this paper' : 'Question deleted');
      fetchPaperData();
    } catch (error) {
      console.error('Failed to delete question:', error);
      toast.error('Failed to delete question');
    }
  };

  const handlePublishPaper = async () => {
    if (questions.length === 0) {
      toast.error('Cannot publish an empty paper');
      return;
    }
    
    try {
      await axios.put(`${API}/written/papers/${paperId}`, { status: 'published' });
      toast.success('Paper published successfully');
      fetchPaperData();
    } catch (error) {
      console.error('Failed to publish paper:', error);
      toast.error('Failed to publish paper');
    }
  };

  const handleUnpublishPaper = async () => {
    try {
      await axios.put(`${API}/written/papers/${paperId}`, { status: 'draft' });
      toast.success('Paper unpublished');
      fetchPaperData();
    } catch (error) {
      console.error('Failed to unpublish paper:', error);
      toast.error('Failed to unpublish paper');
    }
  };

  // Called by PictureManagerUploadDialog when each image upload succeeds
  const handlePMImageAdded = (image) => {
    const key = pmUploadTarget === 'question' ? 'question_images' : 'answer_images';
    setQuestionForm(prev => {
      if (prev[key].length >= 4) {
        toast.error('Maximum 4 images allowed');
        return prev;
      }
      return {
        ...prev,
        [key]: [
          ...prev[key],
          { id: image.id, url: image.url, caption: image.caption || '', order: prev[key].length }
        ]
      };
    });
  };

  // Handle adding image from library
  const handleAddFromLibrary = (image) => {
    const currentImages = imagePickerType === 'question' ? questionForm.question_images : questionForm.answer_images;
    if (currentImages.length >= 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }
    
    if (imagePickerType === 'question') {
      setQuestionForm({
        ...questionForm,
        question_images: [...questionForm.question_images, image]
      });
    } else {
      setQuestionForm({
        ...questionForm,
        answer_images: [...questionForm.answer_images, image]
      });
    }
    toast.success('Image added from library');
  };

  // Remove image
  const removeImage = (type, imageId) => {
    if (type === 'question') {
      setQuestionForm({
        ...questionForm,
        question_images: questionForm.question_images.filter(img => img.id !== imageId)
      });
    } else {
      setQuestionForm({
        ...questionForm,
        answer_images: questionForm.answer_images.filter(img => img.id !== imageId)
      });
    }
  };

  // Update image caption
  const updateImageCaption = (type, imageId, caption) => {
    if (type === 'question') {
      setQuestionForm({
        ...questionForm,
        question_images: questionForm.question_images.map(img =>
          img.id === imageId ? { ...img, caption } : img
        )
      });
    } else {
      setQuestionForm({
        ...questionForm,
        answer_images: questionForm.answer_images.map(img =>
          img.id === imageId ? { ...img, caption } : img
        )
      });
    }
  };

  // Get topic name
  const getTopicName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : null;
  };

  // Multi-topic helpers — mirror the oral admin pattern
  const handleAddTopic = (topicId) => {
    if (!questionForm.topic_ids.includes(topicId)) {
      setQuestionForm({
        ...questionForm,
        topic_ids: [...questionForm.topic_ids, topicId],
      });
    }
    setTopicSearchQuery('');
  };

  const handleRemoveTopic = (topicId) => {
    setQuestionForm({
      ...questionForm,
      topic_ids: questionForm.topic_ids.filter(id => id !== topicId),
    });
  };

  // Build a depth-ordered list of topics (iter 105: recursive so
  // grandchild+ topics are selectable at their correct visual depth).
  const getHierarchicalTopics = () => {
    if (!topics || topics.length === 0) return [];
    const byParent = new Map();
    topics.forEach((t) => {
      const p = t.parent_topic_id || null;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(t);
    });
    const ordered = [];
    const walk = (node, depth, parentName) => {
      ordered.push({ ...node, depth, parent_name: parentName });
      (byParent.get(node.id) || []).forEach((child) => walk(child, depth + 1, node.name));
    };
    (byParent.get(null) || []).forEach((root) => walk(root, 0, null));

    // Orphans (parent_topic_id points outside `topics` scope) — surface
    // at depth 0 so they stay selectable.
    const seen = new Set(ordered.map((o) => o.id));
    topics.forEach((t) => {
      if (!seen.has(t.id)) ordered.push({ ...t, depth: 0 });
    });
    return ordered;
  };

  // Get image URL with backend prefix if needed
  const getImageUrl = (img) => {
    if (!img || !img.url) return '';
    if (img.url.startsWith('http')) return img.url;
    return `${BACKEND_URL}${img.url}`;
  };

  // Calculate answered count
  const answeredCount = questions.filter(q => q.is_answered).length;
  
  // Calculate unanswered count
  const unansweredCount = questions.filter(q => !q.is_answered).length;
  
  // Calculate no topic count — a question has no topics if BOTH the legacy
  // topic_id is empty AND the new topic_ids array is empty.
  const hasNoTopics = (q) => !q.topic_id && (!q.topic_ids || q.topic_ids.length === 0);
  const noTopicCount = questions.filter(hasNoTopics).length;
  
  // Calculate flagged count
  const flaggedCount = flaggedQuestionIds.length;
  
  // Filter questions based on active filter
  const filteredQuestions = (() => {
    let list = activeFilter ? questions.filter(q => {
      switch (activeFilter) {
        case 'answered':
          return q.is_answered;
        case 'unanswered':
          return !q.is_answered;
        case 'flagged':
          return flaggedQuestionIds.includes(q.id);
        case 'no_topic':
          return hasNoTopics(q);
        case 'needs_review':
          return !!q.needs_review;
        default:
          return true;
      }
    }) : questions;
    // Iter 113: Category sub-filter — applied on top of Needs Review filter.
    if (reviewEnabled && activeFilter === 'needs_review') {
      if (reviewCategoryFilter === 'uncategorized') {
        list = list.filter((q) => !q.review_category_id);
      } else if (reviewCategoryFilter && reviewCategoryFilter !== 'all') {
        list = list.filter((q) => q.review_category_id === reviewCategoryFilter);
      }
    }
    return list;
  })();
  const needsReviewCount = questions.filter((q) => q.needs_review).length;
  
  // Toggle filter handler
  const toggleFilter = (filter) => {
    setActiveFilter(prev => prev === filter ? null : filter);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  if (!paper) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Paper not found</p>
          <Button onClick={() => navigate('/admin/written')} className="mt-4">
            Back to Written Management
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/written')}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Papers
              </Button>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-[#0A7EA4]">{paper.grade}</Badge>
              <Badge variant="outline">{paper.function_display}</Badge>
              <Badge 
                variant="outline" 
                className={paper.status === 'published' 
                  ? 'border-green-500 text-green-600' 
                  : 'border-amber-500 text-amber-600'}
              >
                {paper.status === 'published' ? (
                  <><CheckCircle className="w-3 h-3 mr-1" /> Published</>
                ) : (
                  <><Clock className="w-3 h-3 mr-1" /> Draft</>
                )}
              </Badge>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {paper.exam_month}{paper.paper_number ? ` - Paper ${paper.paper_number}` : ''}
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-gray-600">{paper.function_name}</span>
              {paper.total_marks && (
                <span className="text-gray-600">{paper.total_marks} total marks</span>
              )}
            </div>
            
            {/* Clickable Filter Tags */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-xs text-gray-500 mr-1">Filter by:</span>
              
              {/* Answered filter */}
              <Badge 
                onClick={() => toggleFilter('answered')}
                className={cn(
                  "text-sm px-3 py-1 cursor-pointer transition-all",
                  activeFilter === 'answered'
                    ? 'bg-green-600 text-white ring-2 ring-green-400 ring-offset-1'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                )}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                {answeredCount} answered
              </Badge>
              
              {/* Unanswered filter */}
              <Badge 
                onClick={() => toggleFilter('unanswered')}
                className={cn(
                  "text-sm px-3 py-1 cursor-pointer transition-all",
                  activeFilter === 'unanswered'
                    ? 'bg-amber-600 text-white ring-2 ring-amber-400 ring-offset-1'
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                )}
              >
                <Clock className="w-3.5 h-3.5 mr-1" />
                {unansweredCount} unanswered
              </Badge>
              
              {/* Flagged filter - only show if > 0 */}
              {flaggedCount > 0 && (
                <Badge 
                  onClick={() => toggleFilter('flagged')}
                  className={cn(
                    "text-sm px-3 py-1 cursor-pointer transition-all",
                    activeFilter === 'flagged'
                      ? 'bg-red-600 text-white ring-2 ring-red-400 ring-offset-1'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  )}
                >
                  <Flag className="w-3.5 h-3.5 mr-1" />
                  {flaggedCount} flagged
                </Badge>
              )}
              
              {/* No topic filter - only show if > 0 */}
              {noTopicCount > 0 && (
                <Badge 
                  onClick={() => toggleFilter('no_topic')}
                  className={cn(
                    "text-sm px-3 py-1 cursor-pointer transition-all",
                    activeFilter === 'no_topic'
                      ? 'bg-purple-600 text-white ring-2 ring-purple-400 ring-offset-1'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  )}
                >
                  {noTopicCount} no topic
                </Badge>
              )}

              {/* Iter 113: Needs Review chip on paper detail */}
              {reviewEnabled && (
                <Badge
                  onClick={() => toggleFilter('needs_review')}
                  className={cn(
                    "text-sm px-3 py-1 cursor-pointer transition-all inline-flex items-center gap-1",
                    activeFilter === 'needs_review'
                      ? 'bg-amber-600 text-white ring-2 ring-amber-400 ring-offset-1'
                      : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  )}
                  data-testid="written-needs-review-chip"
                >
                  <ClipboardPen className="w-3.5 h-3.5" />
                  {needsReviewCount} needs review
                </Badge>
              )}
              
              {/* Clear filter button */}
              {activeFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setActiveFilter(null); setReviewCategoryFilter('all'); }}
                  className="h-7 px-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Iter 113: Category sub-filter — shown only when Needs Review is active */}
            {reviewEnabled && activeFilter === 'needs_review' && reviewCategories.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mt-2 pl-1" data-testid="written-review-category-filter">
                <span className="text-xs text-gray-500">Category:</span>
                {[{ id: 'all', name: 'All' }, { id: 'uncategorized', name: 'Uncategorized' }, ...reviewCategories].map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setReviewCategoryFilter(c.id)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all",
                      reviewCategoryFilter === c.id
                        ? 'bg-amber-600 text-white'
                        : 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100'
                    )}
                    data-testid={`written-review-cat-${c.id}`}
                  >
                    {c.color && <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />}
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {paper.status === 'draft' ? (
              <Button 
                onClick={handlePublishPaper}
                disabled={questions.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Publish Paper
              </Button>
            ) : (
              <Button 
                variant="outline"
                onClick={handleUnpublishPaper}
                className="text-amber-600 border-amber-600"
              >
                <Clock className="w-4 h-4 mr-2" /> Unpublish
              </Button>
            )}
          </div>
        </div>

        {/* Questions Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Questions
                {activeFilter ? (
                  <span className="text-base font-normal text-gray-500">
                    (showing {filteredQuestions.length} of {questions.length})
                  </span>
                ) : (
                  <span className="text-base font-normal text-gray-500">
                    ({questions.length})
                  </span>
                )}
              </CardTitle>
            </div>
            <Button onClick={openAddQuestionModal} className="bg-[#14B8A6] hover:bg-[#14B8A6]/90">
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </CardHeader>
          <CardContent>
            {questions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions added yet</p>
                <p className="text-sm mt-1">Click &quot;Add Question&quot; to start adding questions</p>
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions match the selected filter</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setActiveFilter(null)}
                  className="mt-3"
                >
                  Clear Filter
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQuestions.map((question, index) => (
                  <div 
                    key={question.id}
                    data-question-id={question.id}
                    className={`p-4 border rounded-lg hover:bg-gray-50 transition-colors ${
                      flaggedQuestionIds.includes(question.id) ? 'border-red-300 bg-red-50/30' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="font-bold text-[#0A7EA4]">Q{question.question_number}.</span>
                          {question.marks && (
                            <Badge variant="outline" className="text-xs">
                              {question.marks} marks
                            </Badge>
                          )}
                          {/* Flagged badge - show if question has pending reports */}
                          {flaggedQuestionIds.includes(question.id) && (
                            <Badge variant="static" className="bg-red-100 text-red-700 text-xs">
                              <Flag className="w-3 h-3 mr-1" /> Flagged
                            </Badge>
                          )}
                          {/* Iter 113: Needs Review badge */}
                          {reviewEnabled && (
                            <ReviewBadge
                              question={question}
                              category={getReviewCategory(question.review_category_id)}
                              dataTestId={`written-review-badge-${question.id}`}
                            />
                          )}
                          {/* Topic badges - render every topic linked to this question */}
                          {(() => {
                            const linked = question.topic_ids && question.topic_ids.length > 0
                              ? question.topic_ids
                              : (question.topic_id ? [question.topic_id] : []);
                            if (linked.length === 0) {
                              return (
                                <Badge variant="outline" className="text-xs text-purple-600 border-purple-300">
                                  No Topic
                                </Badge>
                              );
                            }
                            return linked.map(tid => (
                              <Badge
                                key={tid}
                                variant="static"
                                className="bg-purple-100 text-purple-800 text-xs"
                              >
                                {getTopicName(tid) || 'Unknown topic'}
                              </Badge>
                            ));
                          })()}
                          {question.asked_count > 1 && (
                            question.appearances?.length > 0 ? (
                              <HoverCard openDelay={100} closeDelay={150}>
                                <HoverCardTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border cursor-help bg-blue-50 text-blue-700 border-blue-300"
                                    data-testid={`asked-count-hover-${question.id}`}
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Asked {question.asked_count}x
                                    <Info className="w-3 h-3 ml-0.5 opacity-70" />
                                  </button>
                                </HoverCardTrigger>
                                <HoverCardContent
                                  side="top"
                                  className="w-auto min-w-[180px] p-3 bg-gray-900 text-white border-gray-700"
                                  data-testid={`asked-count-hover-content-${question.id}`}
                                >
                                  <p className="font-medium mb-2 text-xs uppercase tracking-wide text-gray-300">
                                    Appears in {question.appearances.length} paper{question.appearances.length > 1 ? 's' : ''}
                                  </p>
                                  <ul className="space-y-1.5">
                                    {question.appearances.map((app, i) => (
                                      <li key={i}>
                                        <button
                                          type="button"
                                          onClick={() => navigate(`/admin/written/${app.paper_id}`)}
                                          className="flex items-center gap-1.5 text-xs text-white hover:text-blue-300 transition-colors group w-full text-left"
                                          title={`Open paper ${app.paper_id}`}
                                        >
                                          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                          <span>
                                            {app.exam_month || 'Unknown date'}
                                            {app.paper_number ? ` — Paper ${app.paper_number}` : ''}
                                          </span>
                                          <span className="ml-auto text-gray-500 group-hover:text-blue-400 text-[10px]">→</span>
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </HoverCardContent>
                              </HoverCard>
                            ) : (
                              <Badge variant="static" className="bg-blue-100 text-blue-800 text-xs">
                                <RefreshCw className="w-3 h-3 mr-1" /> Asked {question.asked_count}x
                              </Badge>
                            )
                          )}
                          {question.is_answered ? (
                            <Badge variant="static" className="bg-green-100 text-green-800 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" /> Answered
                            </Badge>
                          ) : (
                            <Badge variant="static" className="bg-amber-100 text-amber-800 text-xs">
                              <Clock className="w-3 h-3 mr-1" /> Not Answered
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap">{question.question_text}</p>
                        
                        {/* Question Images */}
                        {question.question_images?.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {question.question_images.map((img, i) => (
                              <img 
                                key={i} 
                                src={getImageUrl(img)} 
                                alt={`Q${question.question_number} image ${i+1}`}
                                className="h-20 rounded border cursor-pointer hover:opacity-80"
                                onClick={() => window.open(getImageUrl(img), '_blank')}
                              />
                            ))}
                          </div>
                        )}
                        
                        {/* Answer Preview */}
                        {question.answer_text && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg">
                            <p className="text-sm font-medium text-green-800 mb-1">Answer:</p>
                            <p className="text-sm text-green-700 line-clamp-2">{question.answer_text}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingQuestion(question)}
                          className="text-[#0A7EA4]"
                          title="View Question"
                          data-testid={`view-question-btn-${question.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditQuestionModal(question)}
                          className="text-[#0A7EA4]"
                          data-testid={`edit-question-${question.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* Delete button — full admins only (backend blocks sub-admins with 403). */}
                        {isFullAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-600"
                            data-testid={`delete-question-${question.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Question Modal */}
        <QuestionViewModal
          question={viewingQuestion}
          kind="written"
          open={!!viewingQuestion}
          onClose={() => setViewingQuestion(null)}
          onEdit={() => openEditQuestionModal(viewingQuestion)}
          helpers={{
            getTopicName,
            getReviewCategory,
          }}
          reviewEnabled={reviewEnabled}
          paperGrade={paper?.grade}
          paperFunction={paper?.function_display || paper?.function_name}
        />

        {/* Question Modal */}
        <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
          <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">

            {/* ── Fullscreen answer expand overlay (inside dialog stacking context) ── */}
            {answerExpanded && (
              <div className="absolute inset-0 z-50 bg-white flex flex-col rounded-lg" data-testid="written-answer-expand-overlay">
                <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 flex-shrink-0 rounded-t-lg">
                  <span className="font-semibold text-gray-800 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-purple-600" />
                    Answer — Q{editingQuestion?.question_number}
                    {paper?.grade && <span className="text-gray-400 font-normal text-sm">· {paper.grade}</span>}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">{questionForm.answer_text?.length || 0} chars</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setAnswerExpanded(false)}
                      className="text-gray-600 hover:text-gray-900"
                      data-testid="written-answer-expand-close"
                    >
                      <Minimize2 className="w-4 h-4 mr-1.5" />
                      Done
                    </Button>
                  </div>
                </div>
                <div className="flex-1 p-5 min-h-0">
                  <Textarea
                    value={questionForm.answer_text}
                    onChange={(e) => setQuestionForm({ ...questionForm, answer_text: e.target.value })}
                    placeholder="Enter the answer..."
                    className="h-full w-full resize-none text-sm leading-relaxed"
                    autoFocus
                    data-testid="written-answer-expand-textarea"
                  />
                </div>
                <div className="px-5 py-2.5 border-t bg-gray-50 text-xs text-gray-400 flex-shrink-0 rounded-b-lg">
                  Tip: Add links using [Link Text](url) format · Changes sync automatically with the modal
                </div>
              </div>
            )}
            <DialogHeader>
              <DialogTitle>
                {editingQuestion ? `Edit Question ${editingQuestion.question_number}` : 'Add Question'}
              </DialogTitle>
              {editingQuestion?.asked_count > 1 && (
                <DialogDescription className="flex items-center gap-2 text-amber-600">
                  <AlertTriangle className="w-4 h-4" />
                  This question appears in {editingQuestion.asked_count} papers. Changes will affect all papers.
                </DialogDescription>
              )}
              {editingQuestion?.id && (
                <div
                  className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500"
                  data-testid="written-edit-question-id"
                >
                  <span className="font-medium text-gray-600">Question ID:</span>
                  <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-[11px] text-gray-800 select-all">
                    {editingQuestion.id}
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(editingQuestion.id);
                      toast.success('Question ID copied');
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                    title="Copy ID"
                    data-testid="written-edit-question-id-copy"
                  >
                    copy
                  </button>
                  {reviewEnabled && editingQuestion?.id && (
                    <ReviewChip
                      question={editingQuestion}
                      kind="written"
                      categories={reviewCategories}
                      enabled={reviewEnabled}
                      onSaved={(updated) => {
                        setEditingQuestion((prev) => (prev ? { ...prev, ...updated } : prev));
                        setQuestions((prev) => prev.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)));
                      }}
                    />
                  )}
                </div>
              )}
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Question Text with Check Duplicates */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Question Text *</Label>
                  {!editingQuestion && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCheckDuplicates}
                      disabled={checkingDuplicates || questionForm.question_text.length < 10}
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      {checkingDuplicates ? (
                        <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Checking...</>
                      ) : (
                        <><Search className="w-4 h-4 mr-1" /> Check Duplicates</>
                      )}
                    </Button>
                  )}
                </div>
                <Textarea
                  value={questionForm.question_text}
                  onChange={(e) => {
                    setQuestionForm({ ...questionForm, question_text: e.target.value });
                    // Hide duplicates panel when text changes
                    if (showDuplicatesPanel) setShowDuplicatesPanel(false);
                  }}
                  placeholder="Enter the question..."
                  rows={4}
                  className="mt-1"
                />
              </div>
              
              {/* Similar Questions Panel */}
              {showDuplicatesPanel && (
                <div className="border rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2">
                      <Copy className="w-4 h-4" />
                      Similar Questions Found ({similarQuestions.length})
                      {similarMeta.threshold_used != null && (
                        <span className="text-xs font-normal text-blue-600" data-testid="written-dup-threshold-label">
                          · threshold {similarMeta.threshold_used}%
                        </span>
                      )}
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDuplicatesPanel(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {similarMeta.skipped_no_embedding > 0 && (
                    <div
                      className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                      data-testid="written-dup-skipped-banner"
                    >
                      ⚠️ {similarMeta.skipped_no_embedding} of {similarMeta.candidates_total} questions were skipped because they don&apos;t have a cached embedding yet.
                      Run <strong>Backfill</strong> in <em>Admin → Embeddings Management</em> to include them in future checks.
                    </div>
                  )}
                  {similarQuestions.length === 0 ? (
                    <p className="text-sm text-blue-700">
                      ✅ No similar questions found. This appears to be unique!
                    </p>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {similarQuestions.map((sq, idx) => {
                        const dupId = sq.question.id || `idx-${idx}`;
                        const isExpanded = expandedDupIds.has(dupId);
                        const toggleExpand = () => {
                          setExpandedDupIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(dupId)) next.delete(dupId);
                            else next.add(dupId);
                            return next;
                          });
                        };
                        return (
                        <div key={dupId} className="bg-white p-3 rounded border">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge variant="static" className={`text-xs ${
                                  sq.similarity_score >= 80 ? 'bg-red-100 text-red-800' :
                                  sq.similarity_score >= 65 ? 'bg-amber-100 text-amber-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {Number(sq.similarity_score).toFixed(1)}% semantic
                                </Badge>
                                {/* First appearance month */}
                                {sq.question.appearances && sq.question.appearances.length > 0 && (
                                  <Badge variant="outline" className="text-xs flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {sq.question.appearances[0].exam_month}
                                  </Badge>
                                )}
                                {/* Multiple appearances - show info icon with hover card */}
                                {sq.question.appearances && sq.question.appearances.length > 1 && (
                                  <HoverCard openDelay={100} closeDelay={100}>
                                    <HoverCardTrigger asChild>
                                      <button 
                                        type="button"
                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border cursor-help bg-amber-50 text-amber-700 border-amber-300"
                                      >
                                        <Info className="w-3 h-3" />
                                        {sq.question.appearances.length}x
                                      </button>
                                    </HoverCardTrigger>
                                    <HoverCardContent 
                                      side="top" 
                                      className="w-auto p-3 bg-gray-900 text-white border-gray-700"
                                    >
                                      <p className="font-medium mb-2 text-sm">Asked in:</p>
                                      <ul className="text-xs space-y-1">
                                        {sq.question.appearances.map((app, i) => (
                                          <li key={i} className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3 text-gray-400" />
                                            {app.exam_month}{app.paper_number ? ` (Paper ${app.paper_number})` : ''}
                                          </li>
                                        ))}
                                      </ul>
                                    </HoverCardContent>
                                  </HoverCard>
                                )}
                                {sq.question.is_answered && (
                                  <Badge variant="static" className="bg-green-100 text-green-800 text-xs">
                                    Has Answer
                                  </Badge>
                                )}
                              </div>
                              {/* Question text — clamped by default, expandable
                                  because written questions are LONG and admins
                                  need to compare the FULL text to decide
                                  duplicate vs. related. */}
                              <p
                                className={`text-sm text-gray-700 ${
                                  isExpanded
                                    ? 'whitespace-pre-wrap'
                                    : 'line-clamp-2'
                                }`}
                                data-testid={`dup-question-text-${dupId}`}
                              >
                                {sq.question.question_text}
                              </p>
                              {/* Show/Hide toggle — always visible; matches the
                                  same UX admins already use on the report
                                  cards elsewhere in this file. */}
                              <button
                                type="button"
                                onClick={toggleExpand}
                                className="mt-1 text-[11px] text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                                data-testid={`dup-toggle-${dupId}`}
                              >
                                {isExpanded ? (
                                  <>Show less <ChevronDown className="w-3 h-3 rotate-180 transition-transform" /></>
                                ) : (
                                  <>Show full question <ChevronDown className="w-3 h-3 transition-transform" /></>
                                )}
                              </button>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openLinkQuestionModal(sq)}
                              className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 shrink-0"
                            >
                              <Link2 className="w-4 h-4 mr-1" /> Use This
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              {/* Question Images */}
              <div>
                <Label className="flex items-center gap-2">
                  <Image className="w-4 h-4" /> Question Images (Max 4, 300KB each)
                </Label>
                
                {questionForm.question_images.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {questionForm.question_images.map((img, index) => (
                      <div key={img.id} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                        <div className="relative group shrink-0">
                          <img 
                            src={getImageUrl(img)} 
                            alt={`Question ${index + 1}`} 
                            className="h-16 w-16 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage('question', img.id)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="flex-1 min-w-0">
                          <Input
                            placeholder="Add caption for this image..."
                            value={img.caption || ''}
                            onChange={(e) => updateImageCaption('question', img.id, e.target.value)}
                            className="text-sm h-8"
                          />
                          <p className="text-xs text-gray-500 mt-1">Image {index + 1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {questionForm.question_images.length < 4 && (
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setPmUploadTarget('question'); setPmUploadOpen(true); }}
                      data-testid="upload-question-image-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" /> Upload
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => { setImagePickerType('question'); setImagePickerOpen(true); }}
                    >
                      <FolderOpen className="h-4 w-4 mr-2" /> Library
                    </Button>
                    <span className="text-xs text-gray-500 ml-2 self-center">
                      {questionForm.question_images.length}/4 images
                    </span>
                  </div>
                )}
              </div>
              
              {/* Topic and Marks Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Topics</Label>
                  <div className="mt-1">
                    <TopicPickerMulti
                      topics={topics}
                      value={questionForm.topic_ids}
                      onChange={(ids) => setQuestionForm({ ...questionForm, topic_ids: ids })}
                      placeholder="Tag topics for this question…"
                      testIdPrefix="written-topic-tags"
                    />
                  </div>
                </div>
                <div>
                  <Label>Marks</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g., 10"
                    value={questionForm.marks}
                    onChange={(e) => setQuestionForm({ ...questionForm, marks: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Answer Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-gray-500">Answer (Optional - can add later)</p>
                  
                  {/* Mark as Answered Checkbox */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is-answered"
                      checked={questionForm.is_answered}
                      onCheckedChange={(checked) => setQuestionForm({ ...questionForm, is_answered: checked })}
                    />
                    <Label htmlFor="is-answered" className="text-sm font-medium cursor-pointer text-green-700">
                      Mark as Answered
                    </Label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Answer Text</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnswerExpanded(true)}
                        className="text-gray-500 hover:text-gray-800 hover:bg-gray-100 h-7 text-xs"
                        data-testid="written-answer-expand-btn"
                      >
                        <Maximize2 className="w-3.5 h-3.5 mr-1" />
                        Expand
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowResearchSheet(true)}
                        className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 h-7 text-xs"
                        data-testid="research-sheet-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        Research
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={questionForm.answer_text}
                    onChange={(e) => setQuestionForm({ ...questionForm, answer_text: e.target.value })}
                    placeholder="Enter the answer..."
                    rows={8}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    Supports markdown links: <code className="bg-gray-100 px-1 rounded">[Link Text](https://url.com)</code>
                  </p>
                </div>

                {/* Explainer Video URL (YouTube or Dadan) */}
                <div>
                  <Label htmlFor="written-video-url" className="text-sm font-medium">
                    Explainer video <span className="text-gray-500 font-normal">(optional · YouTube or Dadan)</span>
                  </Label>
                  <Input
                    id="written-video-url"
                    type="text"
                    value={questionForm.video_url || ''}
                    onChange={(e) => setQuestionForm({ ...questionForm, video_url: e.target.value })}
                    placeholder="Paste a YouTube/Dadan URL or <iframe> embed code (dadan.io or video.helmtorq.com)"
                    className="mt-1"
                    data-testid="written-edit-video-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A &quot;Watch video&quot; button will appear next to the answer for paid users.
                    Accepts bare URL, share link, or full iframe embed code.
                  </p>
                </div>
                
                {/* Answer Images */}
                <div className="mt-4">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4" /> Answer Images (Max 4, 300KB each)
                  </Label>
                  
                  {questionForm.answer_images.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {questionForm.answer_images.map((img, index) => (
                        <div key={img.id} className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg">
                          <div className="relative group shrink-0">
                            <img 
                              src={getImageUrl(img)} 
                              alt={`Answer ${index + 1}`} 
                              className="h-16 w-16 object-cover rounded border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage('answer', img.id)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex-1 min-w-0">
                            <Input
                              placeholder="Add caption for this image..."
                              value={img.caption || ''}
                              onChange={(e) => updateImageCaption('answer', img.id, e.target.value)}
                              className="text-sm h-8"
                            />
                            <p className="text-xs text-gray-500 mt-1">Image {index + 1}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {questionForm.answer_images.length < 4 && (
                    <div className="mt-2 flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setPmUploadTarget('answer'); setPmUploadOpen(true); }}
                        data-testid="upload-answer-image-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" /> Upload
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setImagePickerType('answer'); setImagePickerOpen(true); }}
                      >
                        <FolderOpen className="h-4 w-4 mr-2" /> Library
                      </Button>
                      <span className="text-xs text-gray-500 ml-2 self-center">
                        {questionForm.answer_images.length}/4 images
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Reports Section - Only show when editing and question has reports */}
              {editingQuestion && (
                <Collapsible open={showReportsSection} onOpenChange={setShowReportsSection}>
                  <div className={`border rounded-lg overflow-hidden ${
                    questionReports.some(r => r.status === 'pending' || r.status === 'under_review') 
                      ? 'border-red-300' 
                      : 'border-gray-200'
                  }`}>
                    <CollapsibleTrigger className="w-full">
                      <div className={`p-3 flex items-center justify-between hover:bg-opacity-80 transition-colors ${
                        questionReports.some(r => r.status === 'pending' || r.status === 'under_review')
                          ? 'bg-red-50'
                          : 'bg-gray-50'
                      }`}>
                        <div className="flex items-center gap-2">
                          <Flag className={`w-4 h-4 ${
                            questionReports.some(r => r.status === 'pending' || r.status === 'under_review')
                              ? 'text-red-600'
                              : 'text-gray-500'
                          }`} />
                          <span className="font-medium text-sm">
                            User Reports ({questionReports.length})
                          </span>
                          {questionReports.filter(r => r.status === 'pending' || r.status === 'under_review').length > 0 && (
                            <Badge className="bg-red-500 text-white text-xs">
                              {questionReports.filter(r => r.status === 'pending' || r.status === 'under_review').length} pending
                            </Badge>
                          )}
                        </div>
                        {showReportsSection ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 space-y-3 max-h-[250px] overflow-y-auto">
                        {loadingReports ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                          </div>
                        ) : questionReports.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-2">No reports for this question</p>
                        ) : (
                          questionReports.map((report) => (
                            <div 
                              key={report.id} 
                              className={`border rounded-lg p-3 ${
                                report.status === 'pending' || report.status === 'under_review'
                                  ? 'border-red-200 bg-red-50/50'
                                  : 'border-gray-200 bg-gray-50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <Badge 
                                      variant="outline" 
                                      className={`text-xs ${
                                        report.status === 'pending' ? 'border-red-500 text-red-600' :
                                        report.status === 'under_review' ? 'border-amber-500 text-amber-600' :
                                        report.status === 'resolved' ? 'border-green-500 text-green-600' :
                                        'border-gray-500 text-gray-600'
                                      }`}
                                    >
                                      {report.status}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                      {REPORT_TYPE_LABELS[report.report_type] || report.report_type}
                                    </Badge>
                                    <span className="text-xs text-gray-500">
                                      {new Date(report.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700">{report.description}</p>
                                  {report.suggested_correction && (
                                    <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                                      <span className="font-medium text-blue-700">Suggestion: </span>
                                      <span className="text-blue-600">{report.suggested_correction}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                    <User className="w-3 h-3" />
                                    {report.reporter_name || 'Anonymous'} 
                                    {report.reporter_indos && ` (${report.reporter_indos})`}
                                  </div>
                                </div>
                                {(report.status === 'pending' || report.status === 'under_review') && (
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleResolveReport(report.id)}
                                      disabled={resolvingReport === report.id}
                                      className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                                      title="Resolve"
                                    >
                                      {resolvingReport === report.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDismissReport(report.id)}
                                      disabled={resolvingReport === report.id}
                                      className="h-7 w-7 p-0 text-gray-600 hover:bg-gray-100"
                                      title="Dismiss"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowQuestionModal(false)}
                >
                  Cancel
                </Button>
                {!editingQuestion && (
                  <Button 
                    variant="outline"
                    onClick={handleSaveAndAddAnother}
                    disabled={saving}
                    className="text-[#14B8A6] border-[#14B8A6]"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Save & Add Another
                  </Button>
                )}
                {/* Show "Update & Resolve" button when there are pending reports */}
                {editingQuestion && questionReports.some(r => r.status === 'pending' || r.status === 'under_review') && (
                  <Button 
                    onClick={handleUpdateAndResolve}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Update & Resolve Reports
                  </Button>
                )}
                <Button 
                  onClick={handleSaveQuestion}
                  disabled={saving}
                  className="bg-[#0A7EA4] hover:bg-[#0A7EA4]/90 flex-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  {editingQuestion ? 'Update Question' : 'Save as New Question'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Link Question Modal */}
        <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link Existing Question</DialogTitle>
              <DialogDescription>
                This question already exists in the system. Choose how to handle the answer.
              </DialogDescription>
            </DialogHeader>
            
            {selectedSimilarQuestion && (
              <div className="space-y-4 mt-4">
                {/* Question Preview */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Question:</p>
                  <p className="text-sm text-gray-600">{selectedSimilarQuestion.question.question_text}</p>
                  
                  {selectedSimilarQuestion.question.answer_text && (
                    <div className="mt-3 p-2 bg-green-50 rounded">
                      <p className="text-xs font-medium text-green-700 mb-1">Existing Answer:</p>
                      <p className="text-xs text-green-600 line-clamp-3">
                        {selectedSimilarQuestion.question.answer_text}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Answer Action Options */}
                <div>
                  <Label className="mb-3 block">How would you like to handle the answer?</Label>
                  <RadioGroup value={answerAction} onValueChange={setAnswerAction}>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="keep" id="keep" />
                        <Label htmlFor="keep" className="font-normal cursor-pointer">
                          Keep existing answer
                        </Label>
                      </div>
                      {questionForm.answer_text && (
                        <>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="replace" id="replace" />
                            <Label htmlFor="replace" className="font-normal cursor-pointer">
                              Replace with my new answer
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="merge" id="merge" />
                            <Label htmlFor="merge" className="font-normal cursor-pointer">
                              Merge both answers
                            </Label>
                          </div>
                        </>
                      )}
                    </div>
                  </RadioGroup>
                </div>
                
                {/* Info Note */}
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <p>
                    <strong>Note:</strong> Linking this question will:
                  </p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>Add this paper to the question&apos;s history</li>
                    <li>Increment the &quot;asked count&quot; for tracking frequency</li>
                    <li>Any future edits will affect all papers using this question</li>
                  </ul>
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setShowLinkModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleLinkQuestion}
                    disabled={linkingQuestion}
                    className="flex-1 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                  >
                    {linkingQuestion ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Linking...</>
                    ) : (
                      <><Link2 className="w-4 h-4 mr-2" /> Link Question</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Image Picker Modal */}
        <ImagePickerModal
          isOpen={imagePickerOpen}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleAddFromLibrary}
          maxSelections={4 - (imagePickerType === 'question' ? questionForm.question_images.length : questionForm.answer_images.length)}
          currentImages={imagePickerType === 'question' ? questionForm.question_images : questionForm.answer_images}
        />

        <PictureManagerUploadDialog
          open={pmUploadOpen}
          onOpenChange={setPmUploadOpen}
          onImageAdded={handlePMImageAdded}
        />
      </div>

      {/* Research Sheet */}
      <Sheet open={showResearchSheet} onOpenChange={setShowResearchSheet}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px] flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-violet-700">
              <BookOpen className="w-5 h-5" />
              Research
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 mt-4 overflow-hidden">
            <ResearchPanel questionText={questionForm.question_text} currentGrade={paper?.grade} />
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

export default WrittenPaperDetailPage;
